import React from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip from 'component/Layout/Tooltip';

import classNames from 'classnames';
import { Feature } from 'config/features';

import { useFeature } from 'hooks/useFeature';
import { QaIdentifier, useMultipleQa } from 'hooks/useQa';

import { ReactComponent as IconInfo } from 'media/svg/infoCircle.svg';

import { ConditionalWrapper } from 'utility/Check/ConditionalWrapper';
import { getDecimalNumber } from 'utility/Format/Number';
import { getFormattedPrice } from 'utility/Format/Price';

import { FilterMeta } from 'proto/Search/FilterMeta';

import styles from './Segment.module.scss';

interface FilterLabelProps extends Partial<FilterMeta> {
    labelText: string;
    tooltip?: string;
    textOverflow?: boolean;
    customClassList?: {
        Label?: string;
    };
}

const FilterLabel: React.FC<FilterLabelProps> = ({
    labelText,
    hasCount = false,
    count = 0,
    price = 0,
    tooltip,
    customClassList,
    textOverflow = false,
}) => {
    const { t } = useTranslation();
    const [qaAttrCount, qaAttrPrice, qaAttrLabel] = useMultipleQa([
        QaIdentifier.Search_Filter_Section_Label_Count,
        QaIdentifier.Search_Filter_Section_Label_Price,
        QaIdentifier.Search_Filter_Section_Label,
    ]);
    const noFilterPrices = useFeature(Feature.NoFilterPrices);

    return (
        <span
            className={classNames(customClassList?.Label, styles.Label, {
                [styles.TextOverflow]: textOverflow,
            })}
        >
            <span>
                <span className={styles.LabelText}>
                    <ConditionalWrapper
                        condition={!!textOverflow}
                        wrapper={(children: React.ReactElement) => (
                            <span className={styles.TextOverflow}>
                                {children}
                            </span>
                        )}
                    >
                        <span {...qaAttrLabel}>{labelText}</span>
                    </ConditionalWrapper>
                    {tooltip && (
                        <Tooltip
                            align={{
                                offset: [16, 0],
                            }}
                            content={
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: tooltip,
                                    }}
                                />
                            }
                        >
                            <span className={styles.IconWrapper}>
                                &nbsp;
                                <IconInfo className={styles.Icon} />
                            </span>
                        </Tooltip>
                    )}
                </span>
                {!!price && !noFilterPrices && (
                    <small {...qaAttrPrice}>
                        {t('TRANS-from-price', {
                            amount: getFormattedPrice(price),
                        })}
                    </small>
                )}
            </span>
            {hasCount && (
                <span className={styles.OfferCount} {...qaAttrCount}>
                    {getDecimalNumber(count, 0)}
                </span>
            )}
        </span>
    );
};

export default FilterLabel;
